import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Search, SlidersHorizontal } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import AdvancedFilters from './AdvancedFilters';

/**
 * Schnellfilter-Leiste fuer das Lager-Modul (Phase 1 + Phase 3b + Phase 3c).
 * Phase 3b: 'Mehr Filter'-Button blendet AdvancedFilters ein.
 * Phase 3c: user/werk/merkmale/onMerkmaleChange an AdvancedFilters weitergereicht.
 *
 * Props:
 *   warengruppen     Array<{matkl, wgbez, parent_matkl}>
 *   materialarten    Array<string>
 *   selectedWg       Array<string>
 *   selectedMa       Array<string>
 *   bestandsstatus   'mit'|'ohne'|'alle'
 *   onWgChange       (newArray) => void
 *   onMaChange       (newArray) => void
 *   onBestandsstatusChange (val) => void
 *   onReset          () => void
 *   filterOptions    { lagerorte: [], beschaffungsarten: [], dispomerkmale: [] }
 *   advFilters       { lagerort: [], beschaffungsart: [], dispomerkmal: [],
 *                      bestand_min: '', bestand_max: '',
 *                      offene_banf: 'alle', offene_bestellung: 'alle',
 *                      handlungsbedarf: false }
 *   onAdvChange      (patch) => void
 *   // Phase 3c
 *   user             Object
 *   werk             string
 *   merkmale         Array
 *   onMerkmaleChange (merkmale) => void
 */
const MaterialFilters = ({
  warengruppen,
  materialarten,
  selectedWg,
  selectedMa,
  bestandsstatus,
  onWgChange,
  onMaChange,
  onBestandsstatusChange,
  onReset,
  filterOptions,
  advFilters,
  onAdvChange,
  user,
  werk,
  merkmale,
  onMerkmaleChange,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [wgOpen, setWgOpen] = useState(false);
  const [maOpen, setMaOpen] = useState(false);
  const [wgSearch, setWgSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const wgRef = useRef(null);
  const maRef = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (wgRef.current && !wgRef.current.contains(e.target)) setWgOpen(false);
      if (maRef.current && !maRef.current.contains(e.target)) setMaOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const safeWg = Array.isArray(warengruppen) ? warengruppen : [];
  const safeMa = Array.isArray(materialarten) ? materialarten : [];
  const safeSelWg = Array.isArray(selectedWg) ? selectedWg : [];
  const safeSelMa = Array.isArray(selectedMa) ? selectedMa : [];

  const filteredWg = safeWg.filter(
    (w) =>
      !wgSearch ||
      w.wgbez?.toLowerCase().includes(wgSearch.toLowerCase()) ||
      w.matkl?.toLowerCase().includes(wgSearch.toLowerCase())
  );

  const toggleWg = (matkl) => {
    const next = safeSelWg.includes(matkl)
      ? safeSelWg.filter((c) => c !== matkl)
      : [...safeSelWg, matkl];
    onWgChange(next);
  };

  const toggleMa = (code) => {
    const next = safeSelMa.includes(code)
      ? safeSelMa.filter((c) => c !== code)
      : [...safeSelMa, code];
    onMaChange(next);
  };

  // Pruefe ob erweiterte Filter aktiv sind (inkl. Phase 3c Merkmale)
  const safeAdv = advFilters || {};
  const safeMerkmale = Array.isArray(merkmale) ? merkmale : [];
  const hasAdvActive =
    (Array.isArray(safeAdv.lagerort) && safeAdv.lagerort.length > 0) ||
    (Array.isArray(safeAdv.beschaffungsart) && safeAdv.beschaffungsart.length > 0) ||
    (Array.isArray(safeAdv.dispomerkmal) && safeAdv.dispomerkmal.length > 0) ||
    (safeAdv.bestand_min != null && safeAdv.bestand_min !== '') ||
    (safeAdv.bestand_max != null && safeAdv.bestand_max !== '') ||
    (safeAdv.offene_banf && safeAdv.offene_banf !== 'alle') ||
    (safeAdv.offene_bestellung && safeAdv.offene_bestellung !== 'alle') ||
    safeAdv.handlungsbedarf === true ||
    (Array.isArray(safeAdv.stock_type) && safeAdv.stock_type.length > 0) ||
    (Array.isArray(safeAdv.vendor) && safeAdv.vendor.length > 0) ||
    (Array.isArray(safeAdv.ekgrp) && safeAdv.ekgrp.length > 0) ||
    safeAdv.sperrbestand === true ||
    safeAdv.qmbestand === true ||
    safeMerkmale.length > 0;

  const hasActiveFilters =
    safeSelWg.length > 0 || safeSelMa.length > 0 || bestandsstatus !== 'alle' || hasAdvActive;

  const chipBase = `inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none`;
  const chipActive = isDarkMode
    ? 'bg-brand/25 border-brand/50 text-blue-200'
    : 'bg-brand/10 border-brand/40 text-brand';
  const chipInactive = isDarkMode
    ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12'
    : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  const dropdownBase = `absolute z-40 mt-1 w-64 rounded-xl shadow-lg border overflow-hidden ${isDarkMode
    ? 'bg-gray-900 border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
    : 'bg-white border-slate-200 shadow-lg'
  }`;

  return (
    <>
      {/* ---- Schnellfilter-Zeile ---- */}
      <div className={`px-4 py-3 border-b flex flex-wrap items-center gap-2 ${isDarkMode
        ? 'bg-gray-900/30 border-white/10'
        : 'bg-white border-slate-200'
      }`}>

        {/* Warengruppe Dropdown */}
        <div className="relative" ref={wgRef}>
          <button
            type="button"
            onClick={() => { setWgOpen((v) => !v); setMaOpen(false); }}
            className={`${chipBase} ${safeSelWg.length > 0 ? chipActive : chipInactive} gap-1.5`}
          >
            {t('lm.filterWarengruppe')}
            {safeSelWg.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-blue-100' : 'bg-brand text-white'}`}>
                {safeSelWg.length}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${wgOpen ? 'rotate-180' : ''}`} />
          </button>

          {wgOpen && (
            <div className={dropdownBase} style={{ minWidth: 260 }}>
              <div className={`p-2 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
                <div className="relative">
                  <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    value={wgSearch}
                    onChange={(e) => setWgSearch(e.target.value)}
                    placeholder={t('lm.filterWarengruppe')}
                    className={`w-full pl-7 pr-3 py-1.5 text-xs rounded-lg border outline-none ${isDarkMode
                      ? 'bg-white/8 border-white/15 text-white placeholder-white/30'
                      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                    }`}
                  />
                </div>
              </div>
              <div className="max-h-52 overflow-y-auto py-1">
                {filteredWg.length === 0 ? (
                  <p className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                    {t('lm.empty')}
                  </p>
                ) : (
                  filteredWg.map((w) => (
                    <button
                      key={w.matkl}
                      type="button"
                      onClick={() => toggleWg(w.matkl)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${isDarkMode
                        ? 'hover:bg-white/8 text-white/80'
                        : 'hover:bg-slate-50 text-slate-700'
                      } ${safeSelWg.includes(w.matkl) ? (isDarkMode ? 'bg-brand/15' : 'bg-brand/5') : ''}`}
                    >
                      <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${safeSelWg.includes(w.matkl)
                        ? 'bg-brand border-brand'
                        : isDarkMode ? 'border-white/30' : 'border-slate-300'
                      }`}>
                        {safeSelWg.includes(w.matkl) && <X className="w-2.5 h-2.5 text-white" />}
                      </span>
                      <span className="truncate">{w.wgbez || w.matkl}</span>
                      {w.matkl && <span className={`ml-auto text-[10px] flex-shrink-0 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>{w.matkl}</span>}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Materialart Dropdown */}
        <div className="relative" ref={maRef}>
          <button
            type="button"
            onClick={() => { setMaOpen((v) => !v); setWgOpen(false); }}
            className={`${chipBase} ${safeSelMa.length > 0 ? chipActive : chipInactive} gap-1.5`}
          >
            {t('lm.filterMaterialart')}
            {safeSelMa.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-blue-100' : 'bg-brand text-white'}`}>
                {safeSelMa.length}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${maOpen ? 'rotate-180' : ''}`} />
          </button>

          {maOpen && (
            <div className={dropdownBase} style={{ minWidth: 200 }}>
              <div className="max-h-52 overflow-y-auto py-1">
                {safeMa.length === 0 ? (
                  <p className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                    {t('lm.empty')}
                  </p>
                ) : (
                  safeMa.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleMa(code)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${isDarkMode
                        ? 'hover:bg-white/8 text-white/80'
                        : 'hover:bg-slate-50 text-slate-700'
                      } ${safeSelMa.includes(code) ? (isDarkMode ? 'bg-brand/15' : 'bg-brand/5') : ''}`}
                    >
                      <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${safeSelMa.includes(code)
                        ? 'bg-brand border-brand'
                        : isDarkMode ? 'border-white/30' : 'border-slate-300'
                      }`}>
                        {safeSelMa.includes(code) && <X className="w-2.5 h-2.5 text-white" />}
                      </span>
                      <span>{code}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bestandsstatus Toggle-Chips */}
        {['alle', 'mit', 'ohne'].map((val) => (
          <button
            key={val}
            type="button"
            onClick={() => onBestandsstatusChange(val)}
            className={`${chipBase} ${bestandsstatus === val ? chipActive : chipInactive}`}
          >
            {val === 'alle' ? t('lm.bestandAlle') : val === 'mit' ? t('lm.bestandMit') : t('lm.bestandOhne')}
          </button>
        ))}

        {/* Aktive Warengruppen-Chips */}
        {safeSelWg.map((matkl) => {
          const wg = safeWg.find((w) => w.matkl === matkl);
          return (
            <span key={matkl} className={`${chipBase} ${chipActive}`}>
              {wg?.wgbez || matkl}
              <button
                type="button"
                onClick={() => toggleWg(matkl)}
                className="ml-0.5 hover:text-red-400 transition-colors"
                aria-label={t('common.remove')}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}

        {/* Aktive Materialart-Chips */}
        {safeSelMa.map((code) => (
          <span key={code} className={`${chipBase} ${chipActive}`}>
            {code}
            <button
              type="button"
              onClick={() => toggleMa(code)}
              className="ml-0.5 hover:text-red-400 transition-colors"
              aria-label={t('common.remove')}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}

        {/* Mehr Filter Button */}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className={`${chipBase} gap-1.5 ${showAdvanced || hasAdvActive ? chipActive : chipInactive} ml-auto`}
        >
          <SlidersHorizontal className="w-3 h-3" />
          {showAdvanced ? t('lm.lessFilters') : t('lm.moreFilters')}
          {hasAdvActive && !showAdvanced && (
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-blue-100' : 'bg-brand text-white'}`}>
              {[
                safeAdv.lagerort?.length || 0,
                safeAdv.beschaffungsart?.length || 0,
                safeAdv.dispomerkmal?.length || 0,
                safeAdv.bestand_min ? 1 : 0,
                safeAdv.bestand_max ? 1 : 0,
                safeAdv.offene_banf && safeAdv.offene_banf !== 'alle' ? 1 : 0,
                safeAdv.offene_bestellung && safeAdv.offene_bestellung !== 'alle' ? 1 : 0,
                safeAdv.handlungsbedarf ? 1 : 0,
                Array.isArray(safeAdv.stock_type) ? safeAdv.stock_type.length : 0,
                Array.isArray(safeAdv.vendor) ? safeAdv.vendor.length : 0,
                Array.isArray(safeAdv.ekgrp) ? safeAdv.ekgrp.length : 0,
                safeAdv.sperrbestand ? 1 : 0,
                safeAdv.qmbestand ? 1 : 0,
              ].reduce((a, b) => a + b, 0)}
            </span>
          )}
        </button>

        {/* Filter zuruecksetzen (nur sichtbar wenn Schnellfilter aktiv und Advanced ausgeblendet) */}
        {hasActiveFilters && !showAdvanced && (
          <button
            type="button"
            onClick={onReset}
            className={`text-xs font-medium transition-colors ${isDarkMode
              ? 'text-white/40 hover:text-white/70'
              : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {t('lm.filterReset')}
          </button>
        )}
      </div>

      {/* ---- Erweiterter Filterbereich ---- */}
      {showAdvanced && (
        <AdvancedFilters
          filterOptions={filterOptions || { lagerorte: [], beschaffungsarten: [], dispomerkmale: [] }}
          advFilters={safeAdv}
          onChange={onAdvChange}
          onReset={onReset}
          hasAnyActive={hasActiveFilters}
          user={user}
          werk={werk}
          selectedWg={selectedWg}
          merkmale={safeMerkmale}
          onMerkmaleChange={onMerkmaleChange}
        />
      )}
    </>
  );
};

export default MaterialFilters;
